import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle } from 'lucide-react';
import { cpdAPI, errMsg } from '../../lib/cpdRegisterAPI';
import { CPDActivityExtras } from './CPDActivityExtras';
import { CPDCertificateReader } from './CPDCertificateReader';
import { toast } from 'sonner';

const EMPTY = { title: '', provider: '', date: '', start_time: '', end_time: '', hours: '', categories: [], category_hours: {}, activity_type: '',
  evidence_type: '', delivery_method: '', credit_group: '', area_hours: {}, verification: {}, description: '', certificate_number: '' };

const Sel = ({ label, value, onChange, items, testId }) => (
  <div><Label>{label}</Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose" /></SelectTrigger>
      <SelectContent>{items.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
    </Select></div>
);

export const CPDActivityDialog = ({ open, onOpenChange, me, userId, activity, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [elig, setElig] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [override, setOverride] = useState('');
  const [files, setFiles] = useState([]);
  const [docCat, setDocCat] = useState('CPD certificate');
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);
  const owner = activity?.user_id || userId || me.user_id;
  useEffect(() => {
    if (!open) return;
    setF(activity ? { ...EMPTY, ...activity, hours: String(activity.hours) } : EMPTY);
    setChosen(activity ? activity.allocations.map((a) => a.user_framework_id) : null);
    setFiles([]); setDups(null); setOverride('');
  }, [open, activity]);
  useEffect(() => {
    if (!open || !f.activity_type || !f.evidence_type) return;
    cpdAPI.eligibility({ activity_type: f.activity_type, evidence_type: f.evidence_type, user_id: owner }).then((r) => setElig(r.items)).catch(() => {});
  }, [open, f.activity_type, f.evidence_type, owner]);
  const selected = chosen ?? elig.filter((e) => e.eligible).map((e) => e.user_framework_id);
  const features = useMemo(() => {
    const on = elig.filter((e) => selected.includes(e.user_framework_id));
    return {
      areas: on.find((e) => e.features.competency_areas.length)?.features.competency_areas || [],
      checks: on.some((e) => e.features.verified_requirements.length),
      groups: on.flatMap((e) => e.features.credit_groups),
      names: on.filter((e) => e.features.competency_areas.length || e.features.verified_requirements.length).map((e) => e.framework_name),
    };
  }, [elig, selected]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const toggleCat = (c) => setF((x) => ({ ...x, categories: x.categories.includes(c) ? x.categories.filter((y) => y !== c) : [...x.categories, c] }));
  const save = async (submit, ack = false) => {
    setBusy(true);
    const body = { ...f, hours: f.hours === '' ? null : Number(f.hours), user_id: owner, user_framework_ids: selected, override_reason: override || null,
      submit, acknowledge_duplicate: ack, category_hours: Object.fromEntries(Object.entries(f.category_hours).filter(([, v]) => v !== '')) };
    try {
      const a = activity ? await cpdAPI.update(activity.id, body) : await cpdAPI.create(body);
      if (activity && submit && ['draft', 'changes_requested'].includes(a.status)) await cpdAPI.action(a.id, 'submit');
      if (files.length) {
        const up = await cpdAPI.upload(a.id, files, docCat);
        up.rejected?.forEach((r) => toast.error(`${r.filename}: ${r.reason}`));
      }
      toast.success(activity ? 'Activity updated' : 'CPD activity saved');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'POTENTIAL_DUPLICATE') setDups({ matches: d.matches, submit });
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const approvalOn = me.approval_required;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cpd-activity-dialog">
        <DialogHeader><DialogTitle>{activity ? 'Edit CPD activity' : 'Add CPD activity'}</DialogTitle></DialogHeader>
        {dups ? (
          <div className="space-y-3" data-testid="cpd-duplicate-warning">
            <p className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="w-4 h-4" /> Potential duplicate CPD activity detected</p>
            {dups.matches.map((m) => <div key={m.id} className="border rounded-lg p-2 text-sm">{m.title} · {m.provider} · {m.date} · {m.hours} h</div>)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDups(null)} data-testid="cpd-duplicate-back">Go back</Button>
              <Button onClick={() => save(dups.submit, true)} data-testid="cpd-duplicate-confirm">It's not a duplicate – save</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Activity title</Label><Input value={f.title} onChange={set('title')} data-testid="cpd-f-title" /></div>
              <div><Label>Provider</Label><Input list="cpd-providers" value={f.provider} onChange={set('provider')} data-testid="cpd-f-provider" />
                <datalist id="cpd-providers">{me.providers.map((p) => <option key={p} value={p} />)}</datalist></div>
              <div><Label>Date</Label><Input type="date" value={f.date} onChange={set('date')} data-testid="cpd-f-date" /></div>
              <div><Label>Start / end time (optional)</Label><div className="flex gap-2">
                <Input type="time" value={f.start_time || ''} onChange={set('start_time')} data-testid="cpd-f-start" />
                <Input type="time" value={f.end_time || ''} onChange={set('end_time')} data-testid="cpd-f-end" /></div></div>
              <div><Label>Hours (or leave blank to use times)</Label><Input type="number" step="0.25" min="0" value={f.hours} onChange={set('hours')} data-testid="cpd-f-hours" /></div>
              <Sel label="Activity type" value={f.activity_type} onChange={set('activity_type')} items={me.activity_types.map((t) => t.name)} testId="cpd-f-type" />
              <Sel label="Evidence type" value={f.evidence_type} onChange={set('evidence_type')} items={['Verifiable', 'Non-Verifiable', 'N/A']} testId="cpd-f-evidence-type" />
              <Sel label="Delivery method" value={f.delivery_method} onChange={set('delivery_method')} items={me.delivery_methods} testId="cpd-f-delivery" />
              <div><Label>Certificate number</Label><Input value={f.certificate_number || ''} onChange={set('certificate_number')} data-testid="cpd-f-cert" /></div>
            </div>
            <div><Label>Categories</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="cpd-f-categories">
                {me.categories.map((c) => (
                  <button type="button" key={c} onClick={() => toggleCat(c)} data-testid={`cpd-cat-${c.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                    className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${f.categories.includes(c) ? 'bg-[#0055FF] text-white border-[#0055FF]' : 'hover:bg-slate-50'}`}>{c}</button>
                ))}</div>
              {f.categories.length > 1 && (
                <div className="grid sm:grid-cols-3 gap-2 mt-2">{f.categories.map((c) => (
                  <div key={c}><Label className="text-xs">{c} hours</Label><Input type="number" step="0.25" value={f.category_hours[c] ?? ''}
                    onChange={(e) => setF((x) => ({ ...x, category_hours: { ...x.category_hours, [c]: e.target.value } }))} /></div>))}</div>
              )}
            </div>
            <CPDActivityExtras f={f} setF={setF} features={features} />
            <div><Label>Description / learning outcomes</Label><Textarea value={f.description} onChange={set('description')} data-testid="cpd-f-description" /></div>
            {elig.length > 0 && (
              <div className="space-y-2" data-testid="cpd-f-frameworks"><Label>Counts toward</Label>
                {elig.map((e) => (
                  <label key={e.user_framework_id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={selected.includes(e.user_framework_id)} data-testid={`cpd-f-fw-${e.user_framework_id}`}
                      onCheckedChange={(v) => setChosen(v ? [...selected, e.user_framework_id] : selected.filter((x) => x !== e.user_framework_id))} />
                    {e.framework_name} {!e.eligible && <span className="text-xs text-amber-800">– {e.reason}</span>}
                  </label>))}
                {me.tier === 'admin' && elig.some((e) => !e.eligible && selected.includes(e.user_framework_id)) && (
                  <Input placeholder="Override reason (required, audited)" value={override} onChange={(e) => setOverride(e.target.value)} data-testid="cpd-f-override" />)}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 items-end">
              <div><Label>Evidence files (PDF, JPG, PNG, Word, Excel)</Label>
                <Input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={(e) => setFiles([...e.target.files])} data-testid="cpd-f-files" /></div>
              <Sel label="Document type" value={docCat} onChange={setDocCat} items={me.evidence_categories} testId="cpd-f-doccat" />
            </div>
            <CPDCertificateReader file={files.find((x) => /\.pdf$/i.test(x.name)) || files[0]} onApply={(v) => setF((x) => ({ ...x, ...v }))} />
            <DialogFooter className="gap-2">
              {approvalOn && <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="cpd-save-draft">Save draft</Button>}
              <Button disabled={busy} onClick={() => save(true)} data-testid="cpd-save-submit">{approvalOn ? 'Submit for approval' : 'Save'}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
